import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { IContentCalendarSelectedSuggestion } from '../../../../../../models';
import { useUserSession } from '../../../../../../models/hooks/appStateHooks';
import { getUniqueIdForSuggestion } from '../../../../../../models/UiUtils';
import { pollSystemJob } from './systemJobPolling';

const schedulePost = (
	userSession: Api.UserSessionContext,
	post: Api.IBlogPost,
	forUserId?: string,
	contentCalendarSuggestionId?: string,
	impersonationContext?: Api.IImpersonationContext
) => {
	return userSession.webServiceHelper.callAsync<Api.IBlogPost>(
		Api.ImpersonationBroker.composeApiUrl({
			impersonationContext,
			urlPath: `blog`,
			queryParams: {
				forUserId,
				contentCalendarSuggestionId,
			},
		}),
		'PUT',
		post
	);
};

export type ScheduleBlogSuggestionsParams = {
	forUserId?: string;
	cancelToken?: AbortSignal;
	impersonationContext?: Api.IImpersonationContext;
	sendWithCompliance?: boolean;
	sendWithComplianceEmail?: string;
	suggestions: IContentCalendarSelectedSuggestion[];
	templateMap: Record<string, { template: Api.IBlogTemplate; mainImage: Api.IFileAttachmentWithURL }>;
	generatedContent: Record<string, Api.IRawRichTextContentState>;
	autoGeneratedPosts?: Record<string, Api.IBlogPost>;
};

export function useScheduleBlogSuggestions({
	onError,
	onSuccess,
}: {
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: (result: PromiseSettledResult<Api.IBlogPost>[], params: ScheduleBlogSuggestionsParams) => void;
}) {
	const userSession = useUserSession();
	return useMutation({
		mutationFn: ({
			cancelToken,
			forUserId,
			impersonationContext,
			sendWithCompliance = false,
			sendWithComplianceEmail,
			suggestions,
			templateMap,
			generatedContent,
			autoGeneratedPosts,
		}: ScheduleBlogSuggestionsParams) => {
			return Promise.allSettled<Api.IBlogPost | null>(
				suggestions.map(suggestion => {
					const templateData = templateMap[suggestion.templateReference.templateId];
					if (!templateData) return Promise.resolve(null);

					const { template, mainImage } = templateData;
					const suggestionId = getUniqueIdForSuggestion(suggestion);

					let aiGeneratedContent = null;
					if (generatedContent) {
						aiGeneratedContent = generatedContent[suggestionId];
					}

					const autoGeneratedPost = autoGeneratedPosts?.[suggestionId];

					// If we have a pre-generated post and no AI override, schedule directly
					if (autoGeneratedPost && !aiGeneratedContent) {
						const post = { ...autoGeneratedPost };
						post.scheduledSendDate = suggestion.schedule.startDate;
						post.sendWithCompliance = sendWithCompliance;
						post.sendWithComplianceEmail = sendWithComplianceEmail;
						if (!post.mainImage) {
							post.mainImage = mainImage;
						}
						return schedulePost(userSession, post, forUserId, suggestion.id, impersonationContext);
					}

					// Skip only when we already have content: an AI override, or a template with no prompt.
					// Don't gate on calendarId — AI-calendar blogs always have one, so still-generating ones would schedule empty.
					const skipGeneration = Boolean(aiGeneratedContent) || !template.prompt?.document;

					const payload = aiGeneratedContent ? { ...template, content: aiGeneratedContent } : template;

					if (skipGeneration) {
						payload.prompt = null;
					}

					return userSession.webServiceHelper
						.callAsync<Api.IBlogGenerationSystemJob>(
							Api.ImpersonationBroker.composeApiUrl({
								impersonationContext,
								urlPath: `blog/generateFromTemplate`,
							}),
							'POST',
							payload
						)
						.then(async systemJob => {
							const jobWithPost = systemJob.generatedContent
								? systemJob
								: await pollSystemJob(userSession, systemJob.id, cancelToken);
							if (jobWithPost && jobWithPost.generatedContent && !cancelToken?.aborted) {
								const post = jobWithPost.generatedContent.content;
								post.scheduledSendDate = suggestion.schedule.startDate;
								post.sendWithCompliance = sendWithCompliance;
								post.sendWithComplianceEmail = sendWithComplianceEmail;
								post.mainImage = mainImage;
								return schedulePost(userSession, post, forUserId, suggestion.id, impersonationContext);
							}
							return null;
						});
				})
			);
		},
		onError,
		onSuccess,
	});
}
